import React, { useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { WalkDataContext } from '../../hooks/walkDataContext';
import { createReverb, createPingPongDelay, connectEffects } from './Effects';
import { getTrackMetadataValue } from '../../utility/MetadataFetcher';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faSpinner, faClosedCaptioning } from '@fortawesome/free-solid-svg-icons';
import { Container, Button } from 'react-bootstrap';
import { parseJsonContent, useContentActivator } from '../../utility/ContentActivator';
import TranscriptOverlay from '../../common/overlays/TranscriptOverlay';

const TonePlayer = () => {
  const [isPaused, setIsPaused] = useState(true);
  const [hasEnded, setHasEnded] = useState(true);
  const [player, setPlayer] = useState(null);
  const [Tone, setTone] = useState(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [reverb, setReverb] = useState(null);
  const [pingPongDelay, setPingPongDelay] = useState(null);
  const [trackTitle, setTrackTitle] = useState(null);
  const [trackCover, setTrackCover] = useState(null);
  const pendingActionsRef = useRef([]);

  const [isIOSInitialized, setIsIOSInitialized] = useState(false);
  const iOsAudioRef = useRef(null);
  const lastActivatedTrackRef = useRef(null); // Track which track was last activated

  const { currentTrack, storyMetas, language, updateAudioEffect, audioEffects, setElementState, storyFeatures, setActiveTab, setIsPlaying } = useContext(WalkDataContext);
  const { activateContent } = useContentActivator();

  const [isBufferLoading, setIsBufferLoading] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);


  const transcriptContent = useMemo(() =>
    getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_transcript'),
    [currentTrack, storyMetas, language]
  );

  // Memoize trackAction to prevent re-creating the array on every render
  const trackAction = useMemo(() => {
    const trackActionString = getTrackMetadataValue(currentTrack, storyMetas, 1, 'trackMeta_activatescontent', null);
    return parseJsonContent(trackActionString ?? '[]');
  }, [currentTrack, storyMetas]);

  useEffect(() => {
    const updateTrackInfo = () => {
      setCurrentAudioUrl(getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_audiotrack'));
      setTrackTitle(getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_name', 'storyMeta_name'));
      setTrackCover(getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_cover', 'storyMeta_cover', '/assets/illustrations/placeholder-cover.jpg'));
    };
    updateTrackInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack, storyMetas, language]);



  const initializeTone = useCallback(async () => {
    if (!Tone) {
      if (!isIOSInitialized) {
        // Spiele die leere Audio-Datei ab
        if (iOsAudioRef.current) {
          try {
            await iOsAudioRef.current.play();
          } catch (error) {
            console.error("Fehler beim Abspielen der leeren Audio-Datei:", error);
          }
        }
        setIsIOSInitialized(true);
      }
      const ToneModule = await import('tone');
      await ToneModule.start();
      setTone(ToneModule);
      return ToneModule;
    }
    return Tone;
  }, [Tone, isIOSInitialized]);

  const initializePlayer = useCallback(async (url) => {
    let ToneInstance;
    if (!Tone){
      ToneInstance = await initializeTone();
    }
  
    if (player) {
      player.dispose();
      reverb.dispose();
      pingPongDelay.dispose();
    }
  
    const newReverb = createReverb(ToneInstance.Reverb, audioEffects.reverbDecay, audioEffects.reverbWet, 0.01);  //(decay, wet, preDelay)
    const newPingPongDelay = createPingPongDelay(ToneInstance.PingPongDelay);
  
    // Erstelle den neuen Player
    const newPlayer = new ToneInstance.Player({
      url,
      loop: false,  // Loop deaktiviert
      onload: () => {
        newPlayer.playbackRate = Math.abs(audioEffects.playbackRate);
        newPlayer.reverse = audioEffects.playbackRate < 0 || audioEffects.isReversed;
        setIsPaused(false);
        ToneInstance.Transport.start();
        newPlayer.sync().start();
      },
    });

    newPlayer.onstop = () => {
      handleTrackEnd();
    };
  
    connectEffects(newPlayer, newPingPongDelay, newReverb, false);
  
    setReverb(newReverb);
    setPingPongDelay(newPingPongDelay);
    setPlayer(newPlayer);
    handlePlay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeTone, player]);
  
  
  const handlePlay = useCallback(() => {
    if (player && Tone) {
      // diese Zeile führt oft zu einem Bug der zur Folge hat dass der Player zwei mal hinntereinander gestartet wird... (https://github.com/Tonejs/Tone.js/issues/1175)
      // manchmal habe ich einen Transportfehler bekommen dass die Position nicht negativ sein darf...
      // erste tests: das funktioniert so....tbc
      //Tone.Transport.position = 0.1;  // nicht ganz 0 wegen rundungsfehlern (darf nie negativ sein)
      //
      player.sync().start();
      // reset Effect to default
      updateAudioEffect('playbackRate', 1);
      updateAudioEffect('reverbWet', 0);
      updateAudioEffect('reverbDecay', 0.1);
      updateAudioEffect('isReversed', false);
      updateAudioEffect('isPingPongDelayEnabled', false);

      Tone.Transport.start();

      setIsPaused(false);
      setHasEnded(true);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, Tone]);

  const togglePause = useCallback(() => {

    if (!player) {
      initializePlayer();
      return;
    } 


    if (player && Tone) {
      setIsPaused(prevIsPaused => {
        const newIsPaused = !prevIsPaused;
        if (newIsPaused) {
          Tone.Transport.pause();
        } else {
          Tone.Transport.start();
        }
        return newIsPaused;
      });
    }
  }, [player, Tone, initializePlayer]);

// handleTrackEnd ist leider etwas kompliziert. Das Problem ist dass die States hasEnded und isPaused zum exakten Zeitpunkt von player.onstop evaluiert werden müssen um eine Pause von einem Track ended zu unterscheiden.
// da React keine Komponenten updated während States geänder werden Muss ein Umweg via setTimeout genommen werden

  const handleTrackEnd = useCallback(() => {
    setIsPaused((currentIsPaused) => {
      setHasEnded((currentHasEnded) => {
        if (currentHasEnded && !currentIsPaused) {
          Tone.Transport.stop();
          Tone.Transport.position = 0.1;  // ebenfalls (wie oben) unter Beobachtung
          currentIsPaused = true;
          pendingActionsRef.current = trackAction || [];
          //console.log(Date.now() + ': Track ended.' );
        }
        return currentHasEnded;
      });
      
      // Hier wird das Callback nur ausgeführt, wenn die State-Änderungen abgeschlossen sind
      setTimeout(() => {
        if (hasEnded && pendingActionsRef.current.length > 0) {

        }
      }, 10);
      return currentIsPaused;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Tone, pendingActionsRef]);
  


// im folgenden wird derBuffer des Players ausgetauscht - manchmal funktioniert das laden nicht - mit wacklicger Internetverbindung testen!
const stopAndUnloadCurrentAudio = useCallback(() => {
  return new Promise((resolve) => {
    if (player) {
      setHasEnded(false);
      player.stop();
      player.unsync(); // Sicherstellen, dass der Player unsynced ist
      if (player.buffer) {
        player.buffer.dispose();
        player.buffer = null;
      }
      // Kurze Verzögerung, um sicherzustellen, dass alle Operationen abgeschlossen sind
      setTimeout(resolve, 500); // 500ms sind eigentlich gut viel - evtl weniger
    } else {
      resolve();
    }
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [player, handleTrackEnd]);

const loadAndPlayAudio = useCallback(async () => {
  if (!player || !currentAudioUrl) return;

  setIsBufferLoading(true);
  try {
    await stopAndUnloadCurrentAudio(); // Sicherstellen, dass der alte Track gestoppt und entladen wird

    const newBuffer = await Tone.Buffer.load(currentAudioUrl);
    player.buffer = newBuffer;

    await Tone.loaded(); // Sicherstellen, dass alles geladen ist
    player.onstop = () => {
      handleTrackEnd(() => true);
    };

    // Reset Geschwindigkeit auf Normal bei Track-Wechsel
    updateAudioEffect('playbackRate', 1);
    updateAudioEffect('isReversed', false);

    //player.sync().start(); // Den neuen Track synchronisiert starten - nun in handlePlay
    handlePlay();
  } catch (error) {
    console.error("Fehler beim Laden oder Abspielen des Audios:", error);
  } finally {
    setIsBufferLoading(false);
    setIsPlaying(true);
    setElementState('track', currentTrack.slug, 'active', false);

  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [player, currentAudioUrl]);

useEffect(() => {
  loadAndPlayAudio();
  return () => {
    stopAndUnloadCurrentAudio();
  };
}, [currentAudioUrl, loadAndPlayAudio, stopAndUnloadCurrentAudio]);

// Content activation effect - separate from audio loading
useEffect(() => {
  // Only activate when track is actually playing (not just loaded)
  if (!isPaused && currentTrack && player && trackAction) {
    // Only activate once per track load (using currentAudioUrl as key)
    // This prevents re-activation on re-renders but allows re-activation when switching tracks
    const trackKey = `${currentTrack.slug}-${currentAudioUrl}`;

    if (lastActivatedTrackRef.current === trackKey) {
      return;
    }

    lastActivatedTrackRef.current = trackKey;

    if (trackAction.length > 0) {
      activateContent(trackAction);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isPaused, currentTrack, player, trackAction, currentAudioUrl]);



  useEffect(() => {
      if (player && Tone){
        const isNegative = audioEffects.playbackRate < 0;
        const absSpeed = Math.abs(audioEffects.playbackRate);
        const newReverseState = isNegative || audioEffects.isReversed;

        // Einfach Speed und Richtung setzen - KEIN Position-Wechsel
        // Der Buffer ist bereits reversed, die Wiedergabe läuft nahtlos weiter
        player.playbackRate = absSpeed;
        player.reverse = newReverseState;
      }
      if (reverb){
        reverb.wet.value = audioEffects.reverbWet;
        reverb.decay = audioEffects.reverbDecay;
      }
      if (player && pingPongDelay && reverb) {
        connectEffects(player, pingPongDelay, reverb, audioEffects.isPingPongDelayEnabled);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioEffects, player]);


  const truncateTitle = (title, maxLength) =>
    title && title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;

  return (
    <>
      <div>
        <audio ref={iOsAudioRef} style={{ display: 'none' }}>
          <source src="/assets/audio/empty.mp3" type="audio/mp3" />
        </audio>
        <Container className="ml-150">
          <div className="audio-player-container-small">
            <div className="player-header">
              <div className="track-thumbnail">
                <img className="audio-image-small" onClick={() => {
                  if (storyFeatures.includes('track-details') || storyFeatures.includes('track-details-no-icon')) {
                    setActiveTab('track-details');
                  }
                }} src={trackCover} alt="Cover" />
              </div>
              <div className="controls w-3">
                <Button
                  onClick={togglePause}
                  disabled={isBufferLoading}
                  variant="link"
                  className="p-0"
                  aria-label={isBufferLoading ? "Loading audio" : isPaused ? "Play" : "Pause"}
                >
                  {isBufferLoading ? (
                    <FontAwesomeIcon icon={faSpinner} size="2x" spin aria-hidden="true" />
                  ) : isPaused ? (
                    <FontAwesomeIcon icon={faPlay} size="2x" aria-hidden="true" />
                  ) : (
                    <FontAwesomeIcon icon={faPause} size="2x" aria-hidden="true" />
                  )}
                </Button>
              </div>
              <div className="track-info">
                <div className="track-title-small">{truncateTitle(trackTitle, 30)}</div>
              </div>
              {transcriptContent && (
                <div className="cc-button-container" style={{ marginLeft: 'auto', paddingRight: '0.5rem', paddingLeft: '0.5rem' }}>
                  <button
                    onClick={() => setShowTranscript(true)}
                    aria-label="Show transcript"
                    className="btn btn-link cc-button cc-button-tone"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.5rem' }}
                  >
                    <FontAwesomeIcon icon={faClosedCaptioning} size="lg" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
      <TranscriptOverlay
        visible={showTranscript}
        onClose={() => setShowTranscript(false)}
        trackTitle={trackTitle}
        transcriptContent={transcriptContent}
      />
    </>
  );
};

export default TonePlayer;
